import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Shield, Mail, Lock, Trash2, AlertTriangle, CheckCircle2, LogOut } from "lucide-react";
import { toast } from "sonner";
import apiService from "@/services/apiService";

const DevPage = () => {
  // Auth state
  const [isVerified, setIsVerified] = useState(false);
  const [adminEmail, setAdminEmail] = useState("admin@mystep.ai");
  const [adminPassword, setAdminPassword] = useState("mystep_pass");
  const [verifyLoading, setVerifyLoading] = useState(false);

  // Reset user state
  const [targetEmail, setTargetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [resetResult, setResetResult] = useState<{
    success: boolean;
    deletedFrom: string[];
    message: string;
  } | null>(null);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifyLoading(true);

    try {
      // Validate email domain
      if (!adminEmail.endsWith("@mystep.ai")) {
        toast.error("Email harus menggunakan domain @mystep.ai");
        return;
      }

      const response = await apiService.post<{ verified: boolean; message: string }>("/dev/verify", {
        email: adminEmail,
        password: adminPassword,
      });

      if (response.verified) {
        setIsVerified(true);
        toast.success("Akses terverifikasi!");
      }
    } catch (error: any) {
      const message = error.response?.data?.error?.message || error.message || "Verifikasi gagal";
      toast.error(message);
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResetConfirm = async () => {
    setResetLoading(true);
    setResetResult(null);

    try {
      const response = await apiService.delete<{
        success: boolean;
        deletedFrom: string[];
        message: string;
      }>("/dev/reset-user", {
        data: {
          email: adminEmail,
          password: adminPassword,
          targetEmail: targetEmail,
        },
      });

      setResetResult(response);
      toast.success(`Data user ${targetEmail} berhasil dihapus!`);
      setTargetEmail("");
    } catch (error: any) {
      const message = error.response?.data?.error?.message || error.message || "Reset user gagal";
      toast.error(message);
    } finally {
      setResetLoading(false);
      setShowConfirmDialog(false);
    }
  };

  const handleLogout = () => {
    setIsVerified(false);
    setAdminEmail("");
    setAdminPassword("");
    setTargetEmail("");
    setResetResult(null);
    toast.info("Berhasil logout dari dev page");
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-red-500/10 via-orange-500/5 to-background">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="h-14 w-14 rounded-full bg-red-500/10 flex items-center justify-center">
              <Shield className="h-7 w-7 text-red-500" />
            </div>
          </div>
          <CardTitle className="text-2xl text-red-600">
            Developer Tools
          </CardTitle>
          <CardDescription>
            {isVerified
              ? "Hati-hati! Tindakan di halaman ini bersifat permanen."
              : "Masukkan kredensial untuk mengakses dev tools."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!isVerified ? (
            // Login Form
            <form onSubmit={handleVerify} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-email">Email (@mystep.ai)</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="admin-email"
                    type="email"
                    placeholder="you@mystep.ai"
                    className="pl-10"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    required
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Hanya email dengan domain @mystep.ai yang diterima
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="admin-password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    required
                  />
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-red-600 hover:bg-red-700" 
                disabled={verifyLoading}
              >
                {verifyLoading ? "Memverifikasi..." : "Masuk"}
              </Button>
            </form>
          ) : (
            // Reset User Form
            <div className="space-y-6">
              {/* User Reset Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-red-600">
                  <Trash2 className="h-5 w-5" />
                  <h3 className="font-semibold">Reset User Data</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Menghapus SEMUA data user dari database termasuk profile, check-ins, goals, 
                  team memberships, chat messages, dan semua data terkait lainnya.
                </p>
                <div className="space-y-2">
                  <Label htmlFor="target-email">Email User yang akan dihapus</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="target-email"
                      type="email"
                      placeholder="user@example.com"
                      className="pl-10"
                      value={targetEmail}
                      onChange={(e) => setTargetEmail(e.target.value)}
                    />
                  </div>
                </div>
                <Button
                  onClick={() => {
                    if (!targetEmail) {
                      toast.error("Masukkan email user yang akan dihapus");
                      return;
                    }
                    setShowConfirmDialog(true);
                  }}
                  className="w-full bg-red-600 hover:bg-red-700"
                  disabled={resetLoading || !targetEmail}
                >
                  {resetLoading ? "Menghapus..." : "Erase User Data"}
                </Button>
              </div>

              {/* Result Display */}
              {resetResult && (
                <div className="space-y-3 p-4 rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="font-medium">Reset Berhasil!</span>
                  </div>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    {resetResult.message}
                  </p>
                  {resetResult.deletedFrom.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-green-700 dark:text-green-300">
                        Data dihapus dari:
                      </p>
                      <ul className="text-xs text-green-600 dark:text-green-400 space-y-0.5 max-h-32 overflow-y-auto">
                        {resetResult.deletedFrom.map((table, index) => (
                          <li key={index}>• {table}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Logout Button */}
              <div className="pt-4 border-t">
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="w-full"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout dari Dev Page
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <AlertDialogTitle>Konfirmasi Hapus Data User</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="space-y-3 pt-2">
              <p>
                Apakah Anda yakin ingin menghapus <strong>SEMUA</strong> data untuk user:
              </p>
              <p className="font-mono text-sm bg-muted p-2 rounded">
                {targetEmail}
              </p>
              <p className="text-red-600 dark:text-red-400 font-medium">
                Tindakan ini tidak dapat dibatalkan! Semua data user termasuk profile, 
                check-ins, goals, team memberships, chat messages, dan data terkait 
                lainnya akan dihapus secara permanen.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Ya, Hapus Semua Data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DevPage;
